import { Stack, StackProps, styled } from '@mui/material';
import { keyframes } from '@mui/material/styles';

const LoaderContainer = styled(Stack, { name: 'LoaderContainer' })(({ theme }) => ({
  /* Keep named layout block title visible when loading */
  position: 'absolute',
  height: '100%',
  width: '100%',
  bottom: 0,
  left: 0,
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: theme.zIndex.app.loader,
}));

/* The 1V glyph outline the snake traces. Same path as Navbar/logo.tsx. */
const ONE_V_GLYPH =
  'M13.2634 18.9012V10.1081C13.2634 9.82873 13.0333 9.61507 12.7703 9.61507H10.4693C10.305 9.61507 10.1406 9.69725 10.0584 9.82873L8.64497 11.9489C8.41487 12.2941 8.6614 12.7378 9.07229 12.7378H9.68041C9.95981 12.7378 10.2063 12.9679 10.2063 13.2638V23.2895C10.2063 23.5689 10.4364 23.799 10.7158 23.799H14.0194C14.2166 23.799 14.3974 23.7003 14.496 23.5524L23.3548 10.4697C23.6013 10.0917 23.3383 9.59863 22.8946 9.59863H20.61C20.4292 9.59863 20.2485 9.69725 20.1498 9.84517L13.9043 19.0819C13.7071 19.3778 13.2634 19.2299 13.2634 18.9012Z';

/*
 * Snake geometry, in pathLength-normalized units (the full glyph outline is
 * 100). A bright head dash leads; behind it the tail is tiled from short
 * segments laid exactly end-to-end via dasharray "0 <start> <len> <rest>",
 * each a touch fainter and thinner than the last, so the comet tail fades
 * continuously instead of in visible bands. All dashes share one
 * stroke-dashoffset animation, which keeps them locked together.
 */
const HEAD_LENGTH = 6;
const TAIL_LENGTH = 30;
const TAIL_SEGMENT_COUNT = 24;
const SEGMENT_LENGTH = TAIL_LENGTH / TAIL_SEGMENT_COUNT;

/* Farthest (faintest) segments first so the ones nearer the head draw on top */
const TAIL_SEGMENTS = Array.from({ length: TAIL_SEGMENT_COUNT }, (_, index) => {
  const k = TAIL_SEGMENT_COUNT - index;
  const t = (k - 1) / TAIL_SEGMENT_COUNT; // 0 = right behind the head, ~1 = tail tip
  const start = HEAD_LENGTH + (k - 1) * SEGMENT_LENGTH;

  return {
    key: `tail-${k}`,
    strokeWidth: Number((2.05 - 0.8 * (k / TAIL_SEGMENT_COUNT)).toFixed(2)),
    opacity: Number((0.92 * Math.pow(1 - t, 1.6)).toFixed(3)),
    strokeDasharray: `0 ${start.toFixed(2)} ${SEGMENT_LENGTH.toFixed(2)} ${(
      100 -
      start -
      SEGMENT_LENGTH
    ).toFixed(2)}`,
  };
});

const trace = keyframes`
  from {
    stroke-dashoffset: 0;
  }
  to {
    stroke-dashoffset: 100;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

/* A quiet glow on the ring — one breath per two snake laps */
const breathe = keyframes`
  0%, 100% {
    opacity: 0.14;
    transform: scale(1);
  }
  50% {
    opacity: 0.24;
    transform: scale(1.008);
  }
`;

const Spinner = styled('svg', {
  name: 'Spinner',
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: number }>(({ theme, size }) => ({
  width: size,
  height: size,
  color: theme.palette.primary.main,
  animation: `${fadeIn} 180ms ease-out`,
  '.Spinner-snake': {
    animation: `${trace} 1.9s linear infinite`,
  },
  '.Spinner-head': {
    filter: 'drop-shadow(0 0 2px currentColor)',
  },
  '.Spinner-ring': {
    transformBox: 'fill-box',
    transformOrigin: 'center',
    animation: `${breathe} 3.8s ease-in-out infinite`,
  },
  /* Respect users who prefer reduced motion */
  '@media (prefers-reduced-motion: reduce)': {
    '.Spinner-snake': {
      animation: `${trace} 5s linear infinite`,
    },
    '.Spinner-ring': {
      animation: `${breathe} 10s ease-in-out infinite`,
    },
  },
}));

interface LoaderProps extends StackProps {
  /** Diameter of the spinner in pixels. */
  size?: number;
}

/**
 * Full-area loading indicator: a comet-headed snake traces the outline of the
 * 1V glyph on a continuous loop, inside a faint anchoring ring. Uses the theme
 * primary color so it stays consistent in light and dark mode.
 */
export const Loader = ({ size = 44, ...props }: LoaderProps) => {
  return (
    <LoaderContainer {...props}>
      <Spinner
        size={size}
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Loading"
      >
        {/* Faint anchoring ring, breathing quietly (one breath per two laps) */}
        <circle
          className="Spinner-ring"
          cx="16"
          cy="16"
          r="14.6"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          opacity={0.18}
        />
        {/* The glyph outline the snake runs along, sitting faint underneath */}
        <path
          d={ONE_V_GLYPH}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          strokeLinejoin="round"
          opacity={0.16}
        />
        {/* Comet tail: exactly-tiled dash segments fading toward the tip */}
        {TAIL_SEGMENTS.map((segment) => (
          <path
            key={segment.key}
            className="Spinner-snake"
            d={ONE_V_GLYPH}
            fill="none"
            stroke="currentColor"
            strokeWidth={segment.strokeWidth}
            strokeLinecap="butt"
            strokeLinejoin="round"
            pathLength={100}
            strokeDasharray={segment.strokeDasharray}
            opacity={segment.opacity}
          />
        ))}
        {/* Comet head */}
        <path
          className="Spinner-snake Spinner-head"
          d={ONE_V_GLYPH}
          fill="none"
          stroke="currentColor"
          strokeWidth={2.1}
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength={100}
          strokeDasharray={`${HEAD_LENGTH} ${100 - HEAD_LENGTH}`}
        />
      </Spinner>
    </LoaderContainer>
  );
};
